<template>
  <div>
    <my-header></my-header>

    <div class="content-container-level-1">
      <h2>Edit timetable record</h2>

      <form @submit.prevent="updateRecordWithConfirmation">
        <div class="form-group">
          <label for="quest">Quest</label>
          <input v-model="questAlias" id="quest" class="form-control" type="string" disabled>
        </div>

        <hr>

        <div class="form-group">
          <label for="start-at">Start at:</label>
          <date-time-input id="start-at" v-model="startAt"></date-time-input>
        </div>

        <hr>

        <div class="form-group">
          <h5>Duration</h5>
          <duration-input v-model="duration"></duration-input>
          <p class="mb-3">
            End at<br>
            {{endAt | getMomentStr}}
          </p>
        </div>

        <hr>

        <div class="form-group">
          <label for="minLevel">Min level</label>
          <input v-model="minLevel" id="minLevel" class="form-control" type="number" min="0">
        </div>

        <hr>

        <div class="form-group">
          <h5>Min time until end for start</h5>
          <duration-input v-model="minTimeUntilEndForStart"></duration-input>
          <p class="mb-3">
            Start available until<br>
            {{startAvailableUntil | getMomentStr}}
          </p>
        </div>

        <hr>

        <div class="form-group">
          <label for="minClientVersion">Min client version</label>
          <input v-model="minClientVersion" id="minClientVersion" class="form-control">
          <p class="form-text">
            Min value (forced version): {{forcedVersion}}
          </p>
          <p class="form-text">
            Max value (server version): {{serverVersion}}
          </p>
        </div>

        <hr>

        <b-form-checkbox id="checkbox-for-qa-users" v-model="forQA" class="mb-3">
          For QA only
        </b-form-checkbox>

        <hr>

        <b-button :to="{name: 'daily_quests_timetable'}">Cancel</b-button>
        <b-button type="submit" variant="primary">Save</b-button>
      </form>
    </div>

    <loading-indicator v-if="isLoading"/>
  </div>
</template>

<script>
import { confirmAction } from '../../service/confirm';

import {
  getMomentStr,
  convertVersionCodeToStr,
  getProtoAlias,
} from '../../util';

import notifier from '../../notifier';
import { HTTP } from '../../http-common';

import MyHeader from '../Header.vue';
import LoadingIndicator from '../LoadingIndicator.vue';
import DateTimeInput from '../DateTimeInput.vue';
import DurationInput from '../DurationInput.vue';

export default {
  name: 'EditDailyQuestsTimetableRecord',

  components: {
    MyHeader,
    LoadingIndicator,
    DurationInput,
    DateTimeInput,
  },

  props: {
    recordId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,

      questAlias: '',
      
      startAt: 0,
      duration: 0,
      minLevel: 0,
      minTimeUntilEndForStart: 0,

      forcedVersion: '',
      serverVersion: '',
      minClientVersion: '',
      forQA: true,
    };
  },

  computed: {
    endAt() {
      return this.startAt/1000 + this.duration;
    },

    startAvailableUntil() {
      return this.endAt - this.minTimeUntilEndForStart;
    },
  },

  created() {
    this.loadRecord();
  },

  methods: {
    loadRecord() {
      notifier.deleteSenderNotifications(this);

      this.isLoading = true;

      const url = `/daily_quests/timetable/${this.recordId}`;
      HTTP.get(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        const record = data.record;
        this.initRecordFormFields(record);

        this.forcedVersion = convertVersionCodeToStr(data.forced_version);
        this.serverVersion = convertVersionCodeToStr(data.server_version);
      })
      .catch(e => {
        notifier.showError(e, {sender: this});
      })
      .finally(() => {
        this.isLoading = false;
      });
    },

    initRecordFormFields(record) {
      this.questAlias = getProtoAlias(record.quest_proto_id);
      this.startAt = record.stime*1000;
      this.duration = record.duration;
      this.minLevel = record.min_level;
      this.minTimeUntilEndForStart = record.min_time_until_end_for_start;
      this.minClientVersion = convertVersionCodeToStr(record.min_client_version);
      this.forQA = record.for_qa;
    },

    updateRecordWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const record = this.makeRecord();

      this.confirmRecordUpdating(() => {
        this.doUpdate(record);
      });
    },

    makeRecord() {
      return {
        id: this.recordId,
        startAt: this.startAt/1000,
        duration: this.duration,
        minLevel: this.minLevel,
        minTimeUntilEndForStart: this.minTimeUntilEndForStart,
        minClientVersion: this.minClientVersion,
        forQA: this.forQA,
      };
    },

    confirmRecordUpdating(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to update this record {env}?',
        'Edit',
        onConfirmed,
      );
    },

    doUpdate(record) {
      const data = new FormData();
      data.set('stime', record.startAt);
      data.set('duration', record.duration);
      data.set('min_level', record.minLevel);
      data.set('min_time_until_end_for_start', record.minTimeUntilEndForStart);
      data.set('min_client_version', record.minClientVersion);
      data.set('for_qa', record.forQA);

      HTTP.post(`/daily_quests/timetable/${record.id}`, data)
      .then(response => {
        const result = response.data.result;

        const RESULT_SUCCESS = 1;
        if(result.code === RESULT_SUCCESS) {
          notifier.showSuccessAfterTransition('Daily quests schedule edited.');
          this.$router.push({name: 'daily_quests_timetable'});
          return;
        }

        notifier.showError(result.message, {sender: this});
      })
      .catch(e => {
        notifier.showError(e, {sender: this});
      });
    },
  },

  filters: {
    getMomentStr,
  },
};
</script>
